import { useEffect } from 'react';

/**
 * Only one thing may make sound at a time.
 *
 * Several places make sound — the live button, the editor and the video
 * previews — and each is mounted independently. Coordinating them through component state
 * alone means every new combination is another chance to miss one, and the
 * failure is two voices at once over a control-room monitor.
 *
 * So the rule is enforced where it cannot be missed: whenever any audio element
 * starts, every other one is paused. Each element's own `pause` handler then
 * updates its component, so the buttons still show what is actually happening.
 *
 * The listener is on the document in the capture phase because media events do
 * not bubble.
 */
/** The smallest shape of a media element this rule needs to know about. */
export interface PausableMedia {
  paused: boolean;
  pause: () => void;
}

/**
 * Pauses everything except the one that just started. Separated from the DOM
 * wiring so the rule itself can be tested.
 */
export function pauseOthers(started: unknown, all: Iterable<PausableMedia>): number {
  let paused = 0;
  for (const element of all) {
    if (element === started) continue;
    // Pausing something already paused would fire a needless event, and each
    // one tells a component to update itself.
    if (element.paused) continue;
    element.pause();
    paused += 1;
  }
  return paused;
}

export function useExclusiveAudio(): void {
  useEffect(() => {
    const onPlay = (event: Event): void => {
      const started = event.target;
      if (!(started instanceof HTMLMediaElement)) return;
      pauseOthers(started, document.querySelectorAll('audio, video') as Iterable<HTMLMediaElement>);
    };

    document.addEventListener('play', onPlay, true);
    return () => document.removeEventListener('play', onPlay, true);
  }, []);
}
